"use client";
import { useInstantSearch } from "react-instantsearch";
import { useRouter, useSearchParams } from "next/navigation";
import Icon from "@/components/icons";
import type { PromoBannerProps } from "@/components/PromoBanner/types";
import {
  RESULT_TYPE_URL_PARAM,
  COURSE,
  PROGRAM,
  PRODUCT_TO_RESULT_TYPE_MAP,
  SINGLE_LOB_SEARCH_PAGE_TYPE,
  MULTI_LOB_SEARCH_PAGE_TYPE,
  DEFAULT_SEARCH_BASE_PATH,
} from "@/components/Search/constants";
import SearchResultsLoadingPlaceholder from "./ResultsLoading";
import { SearchVersion } from "@/components/Search/types";
import NoResults, { NoResultsCourses, NoResultsPrograms } from "../NoResults";
import MobileShowAllResultsButtonWrapper from "../MobileShowAllResultsButton/Wrapper";
import SearchHits from "./SearchHits";
import SortedSearchHits from "./SortedSearchHits";
import { useTypedSearchHits } from "../../hooks/useTypedSearchHits";
import type { InlineFypConfig } from "./types";
import useTranslations from "@/i18n/useTranslations";
import { useLocale } from "next-intl";
import { withLocalePrefix } from "@/i18n/withLocalePrefix";

interface SearchHitsProps {
  title?: string;
  facetForLink?: string;
  level?: string;
  promoBannerData?: PromoBannerProps;
  searchVersion?: SearchVersion;
  inlineFypConfig?: InlineFypConfig;
  /** Pathname for "Show (n)" / mobile full-results navigation. Defaults to `/search`. */
  searchBasePath?: string;
}

function SearchHitsContainer({
  title = "",
  facetForLink = "",
  level = "first",
  promoBannerData,
  searchVersion,
  inlineFypConfig,
  searchBasePath = DEFAULT_SEARCH_BASE_PATH,
}: SearchHitsProps) {
  const { items: hits, results } = useTypedSearchHits();
  const { status } = useInstantSearch();
  const router = useRouter();
  const currentSearchParams = useSearchParams();
  const t = useTranslations("Search");
  const locale = useLocale();
  const hitType = hits.at(0)?.type?.at?.(0) || (hits.at(0)?.type as string);
  if (status !== "idle") {
    return <SearchResultsLoadingPlaceholder />;
  } else if (!results?.nbHits) {
    if (!facetForLink) {
      return <NoResults />;
    }
    if (facetForLink === "course") {
      return <NoResultsCourses />;
    }
    if (facetForLink === "program") {
      return <NoResultsPrograms />;
    }
    return null;
  }

  const handleTabClick = () => {
    const params = new URLSearchParams(
      Array.from(currentSearchParams.entries()),
    );
    params.set(RESULT_TYPE_URL_PARAM, facetForLink);
    router.push(
      `${withLocalePrefix(searchBasePath, locale)}?${params.toString()}`,
    );
  };

  const allResultsStylesHorizontalFilter =
    "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 py-4";
  const groupedResultsStyles = "flex flex-nowrap gap-4 py-4";

  const allResultsStylesClass = allResultsStylesHorizontalFilter;

  const cardClassName = "!w-[300px]";

  const eligibleForSortedSearch =
    facetForLink === PRODUCT_TO_RESULT_TYPE_MAP[COURSE] ||
    facetForLink === PRODUCT_TO_RESULT_TYPE_MAP[PROGRAM] ||
    currentSearchParams.get(RESULT_TYPE_URL_PARAM) ===
      PRODUCT_TO_RESULT_TYPE_MAP[COURSE] ||
    currentSearchParams.get(RESULT_TYPE_URL_PARAM) ===
      PRODUCT_TO_RESULT_TYPE_MAP[PROGRAM];

  const searchPageType =
    currentSearchParams.get(RESULT_TYPE_URL_PARAM) ===
      PRODUCT_TO_RESULT_TYPE_MAP[COURSE] ||
    currentSearchParams.get(RESULT_TYPE_URL_PARAM) ===
      PRODUCT_TO_RESULT_TYPE_MAP[PROGRAM]
      ? SINGLE_LOB_SEARCH_PAGE_TYPE
      : MULTI_LOB_SEARCH_PAGE_TYPE;

  return (
    <div className="order-first">
      {title ? (
        <div className="flex justify-between items-center mt-4 mb-2">
          <h3 className="text-lg my-0">{title}</h3>
          {facetForLink && results?.nbHits ? (
            <button
              onClick={handleTabClick}
              className="text-sm flex items-center hover:text-primary-500 transition-colors"
            >
              {t("show")} ({results.nbHits})
              <Icon type="chevron-right" />
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="overflow-x-scroll overflow-y-visible lg:overflow-x-visible scrollbar-thin">
        <div
          className={
            facetForLink ? groupedResultsStyles : allResultsStylesClass
          }
        >
          {eligibleForSortedSearch ? (
            <SortedSearchHits
              hits={hits}
              results={results}
              facetForLink={facetForLink}
              level={level}
              searchPageType={searchPageType}
              promoBannerData={promoBannerData}
              cardClassName={cardClassName}
              inlineFypConfig={inlineFypConfig}
            />
          ) : (
            <SearchHits
              hits={hits}
              results={results}
              facetForLink={facetForLink}
              level={level}
              promoBannerData={promoBannerData}
              cardClassName={cardClassName}
              inlineFypConfig={inlineFypConfig}
            />
          )}
          {facetForLink && results?.nbHits ? (
            <MobileShowAllResultsButtonWrapper
              handleTabClick={handleTabClick}
              results={results}
              title={title}
              hitType={hitType}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SearchHitsContainer;
